import styles from "./ProgressBar.module.css";

interface ProgressBarProps {
	currentIndex: number;
	total: number;
	questionType?: string;
}

export function ProgressBar({ questionType }: ProgressBarProps) {
	// Always show 4 segments
	const totalSegments = 4;

	// Fixed screen groupings for progress bars:
	// Bar 1 (Segment 0): setup-profile (50%) + setup-profile-details (50%)
	// Bar 2 (Segment 1): Temporarily disabled - always 100% filled
	// Bar 3 (Segment 2): fertility-status (33%) + fertility-role (33%) + fertility-journey-types (33%)
	// Bar 4 (Segment 3): needs-most (25%) + how-did-you-hear (25%) + additional-info (25%) + thank-you (25%)

	// Map question types to their fixed positions
	const getProgressByQuestionType = (type: string | undefined): number => {
		switch (type) {
			// Bar 1 - Profile Setup (2 screens)
			// Note: Bar 2 is always filled (100%) since it's temporarily disabled
			case "setup-profile":
				return 37.5; // Bar 1 at 50% (12.5%) + Bar 2 always filled (25%) = 37.5%
			case "setup-profile-details":
				return 50; // Bar 1 at 100% (25%) + Bar 2 always filled (25%) = 50%

			// Bar 3 - Fertility Journey (3 screens)
			// Bars 1 (25%) + Bar 2 (25%) = 50% baseline
			case "fertility-status":
				return 58.33; // 50% baseline + 33% of bar 3 (8.33%) = 58.33%
			case "fertility-role":
				return 66.66; // 50% baseline + 66% of bar 3 (16.66%) = 66.66%
			case "fertility-journey-types":
				return 75; // 50% baseline + 100% of bar 3 (25%) = 75%

			// Bar 4 - Final screens (4 screens including thank you)
			// Bars 1+2+3 = 75% baseline
			case "needs-most":
				return 81.25; // 75% baseline + 25% of bar 4 (6.25%) = 81.25%
			case "how-did-you-hear":
				return 87.5; // 75% baseline + 50% of bar 4 (12.5%) = 87.5%
			case "additional-info":
				return 93.75; // 75% baseline + 75% of bar 4 (18.75%) = 93.75%
			case "thank-you":
				return 100; // All bars filled = 100%

			default:
				return 0;
		}
	};

	const cappedProgress = getProgressByQuestionType(questionType);

	return (
		<div className={styles.container}>
			<div className={styles.segmentedProgressBar}>
				{Array.from({ length: totalSegments }).map((_, index) => {
					const segmentStart = (index / totalSegments) * 100;
					const segmentEnd = ((index + 1) / totalSegments) * 100;
					const isFilled = cappedProgress > segmentStart;
					const fillPercentage = isFilled
						? Math.min(
								((cappedProgress - segmentStart) /
									(segmentEnd - segmentStart)) *
									100,
								100,
							)
						: 0;

					return (
						<div key={index} className={styles.segment}>
							<div className={styles.segmentBackground}>
								{fillPercentage > 0 && (
									<div
										className={styles.segmentFill}
										style={{ width: `${fillPercentage}%` }}
									/>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
